import { MAX_ITEMS } from "./constants";

// Detect the type of ONE token
export function detectToken(raw) {
  const v = raw.trim();
  if (!v) return null;
  if (/^[a-f0-9]{32}$/i.test(v)) return "cleanmd5";
  if (/^\d{1,31}$/.test(v)) return "refid";
  if (/^\d{1,31}-\d{1,31}$/.test(v)) return "refid-range";
  if (/^[a-f0-9]{1,31}$/i.test(v)) return "partial";
  return "invalid";
}

// Split text into unique tokens (newline / comma / space / semicolon)
export function splitTokens(text) {
  const tokens = text
    .split(/[\s,;]+/)
    .map((t) => t.trim())
    .filter(Boolean);

  const seen = new Set();
  const unique = [];
  for (const t of tokens) {
    const k = t.toLowerCase();
    if (!seen.has(k)) {
      seen.add(k);
      unique.push(t);
    }
  }
  return unique;
}

// "123-456" -> { start: 123, end: 456 } (null if descending or unsafe)
export function parseRange(token) {
  const [a, b] = token.split("-").map(Number);
  if (!Number.isSafeInteger(a) || !Number.isSafeInteger(b) || a > b) return null;
  return { start: a, end: b };
}

// -> { tokens, values, total, type, status, bad }
//   tokens = what the user typed, values = what is sent (ranges expanded)
//   status = empty | ok | invalid | partial | mixed | toomany
export function analyseInput(text) {
  const tokens = splitTokens(text);
  const base = { tokens, values: [], total: 0, type: null, bad: [] };
  if (!tokens.length) return { ...base, status: "empty" };

  const kinds = tokens.map(detectToken);

  const bad = tokens.filter(
    (t, i) =>
      kinds[i] === "invalid" ||
      kinds[i] === "partial" ||
      (kinds[i] === "refid-range" && !parseRange(t)) ||
      (kinds[i] === "refid" && !Number.isSafeInteger(Number(t)))
  );
  if (bad.length) {
    return {
      ...base,
      status:
        kinds.includes("partial") && !kinds.includes("invalid")
          ? "partial"
          : "invalid",
      bad,
    };
  }

  // a range is still a Ref ID search
  const types = new Set(kinds.map((k) => (k === "refid-range" ? "refid" : k)));
  if (types.size > 1) return { ...base, status: "mixed" };
  const type = [...types][0];

  if (type === "cleanmd5") {
    const values = tokens.map((t) => t.toLowerCase());
    return {
      ...base,
      values,
      total: values.length,
      type,
      status: values.length > MAX_ITEMS ? "toomany" : "ok",
    };
  }

  // refid: count first so a huge range is never expanded
  let total = 0;
  tokens.forEach((t, i) => {
    if (kinds[i] === "refid-range") {
      const { start, end } = parseRange(t);
      total += end - start + 1;
    } else {
      total += 1;
    }
  });
  if (total > MAX_ITEMS) return { ...base, total, type, status: "toomany" };

  const set = new Set();
  tokens.forEach((t, i) => {
    if (kinds[i] === "refid-range") {
      const { start, end } = parseRange(t);
      for (let n = start; n <= end; n++) set.add(n);
    } else {
      set.add(Number(t));
    }
  });
  const values = [...set];
  return { ...base, values, total: values.length, type, status: "ok" };
}

// user-facing message for a non-ok analysis
export function getInputMessage(input) {
  switch (input.status) {
    case "empty":
      return "Enter Ref IDs, ranges (123-456) or 32-character MD5s.";
    case "partial":
    case "invalid":
      return `Invalid value: ${input.bad.slice(0, 3).join(", ")}${
        input.bad.length > 3 ? "…" : ""
      }. Use Ref IDs, ranges like 123-456 (low to high), or 32-character MD5s.`;
    case "mixed":
      return "Do not mix Ref IDs and MD5s in one search.";
    case "toomany":
      return `Maximum ${MAX_ITEMS} values per search (you entered ${input.total.toLocaleString()}). Use a smaller range.`;
    default:
      return "";
  }
}
